/**
 * The people who use Origin, in Settings' Users and roles group. Backed by:
 *   GET    /v1/users?page_size=&after=         → list (cursor-paginated)
 *   POST   /v1/users:invite                    → invite new user
 *   DELETE /v1/users/{membership_id}           → remove
 *   PATCH  /v1/users/{membership_id}/role      → change role
 *
 * Users (tab "users"): "Invite user" in the page header, a bar with the
 * search, the count and the column picker, then the table. Each row's "…"
 * menu changes the role, resets the password or removes the user, each
 * through a confirm dialog. The caller's own row has no menu: the gateway
 * forbids self-remove and self-demote anyway.
 *
 * Access control (tab "visibility"): the grants the tenant's transports
 * serve, role grants and activity grants, behind tabs when it serves both.
 */
import { createPortal } from "react-dom";
import { EllipsisHorizontalIcon } from "@heroicons/react/24/outline";
import { ROLE_ACTIVITY_ACCESS_NOTE, ROLE_CATALOG, roleLabel } from "../lib/userRoles.js";
import { useCallback, useLayoutEffect, useDeferredValue, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { restGet, restPost, restDelete, authedFetch } from "../lib/restClient.js";
import SectionActions, { SECTION_BUTTON } from "../components/settings/SectionActions.jsx";
import OverflowTooltip from "../components/OverflowTooltip.jsx";
import DataTable from "../components/DataTable.jsx";
import ColumnPicker from "../components/ColumnPicker.jsx";
import OverlayDialog from "../components/OverlayDialog.jsx";
import ConfirmDialog from "../components/ConfirmDialog.jsx";
import Select from "../components/Select.jsx";
import SearchInput from "../components/ui/SearchInput.jsx";
import Button from "../components/ui/Button.jsx";
import Field from "../components/ui/Field.jsx";
import Tabs from "../components/ui/Tabs.jsx";
import Listing from "../components/ui/Listing.jsx";
import { Count } from "../components/ui/TableBar.jsx";
import { ErrorText } from "../components/ui/Notice.jsx";
import { FIELD, ICON_BUTTON, MENU, MENU_ITEM } from "../components/ui/styles.js";
import { useDataTableColumns } from "../lib/columnSelection.js";
import { hasPermission, hasSensitivePermission, useMyPermissions } from "../lib/permissions.js";
import { PERMISSIONS } from "../lib/permissionKeys.js";
import { denialCopy, denialOrRawMessage } from "../lib/denialReason.js";
import { useCurrentUser } from "../lib/userInfo.js";
import { SkeletonLines } from "../components/ui/Skeleton.jsx";
import AccessTab from "../components/identity/AccessTab.jsx";
import ActivityAccessTab from "../components/identity/ActivityAccessTab.jsx";
import UserEffectiveAccessDialog from "../components/identity/UserEffectiveAccessDialog.jsx";
import { listGrantRoles } from "../lib/accessClient.js";
import { listActivityGrants } from "../lib/activityConfiguration.js";
import { getAuthIdentityEpoch, onAuthIdentityChange } from "../lib/authTokenStore.js";
import { DIALOG } from "../lib/analytics/surfaceVocabulary.js";
import { usesLocalAuth } from "../lib/authBootstrap.js";

const NOTE = "m-0 text-[12px] leading-[1.45] text-[color:var(--muted)]";

// ── Schema ───────────────────────────────────────────────────────

// On a narrow screen the columns that matter least fold away first
// (`fold`, 1 first): membership, then name, then role. Email stretches.
const COLUMNS = [
    { id: "email",       label: "Email",      width: 240, flex: true, minWidth: 180 },
    { id: "name",        label: "Name",       width: 180, fold: 2 },
    { id: "role",        label: "Role",       width: 180, fold: 3 },
    { id: "membership",  label: "Membership", width: 220, fold: 1 },
    { id: "_actions",    label: "Actions",    width: 150 },
];

export function getUserManagementColumns(canManageUsers, canInspectAccess = false) {
    return canManageUsers || canInspectAccess ? COLUMNS : COLUMNS.filter((c) => c.id !== "_actions");
}

const PAGE_SIZE = 100;

export { ROLE_CATALOG, roleLabel } from "../lib/userRoles.js";
// Picker offers every built-in. The backend writes the chosen kebab slug
// straight through to WorkOS (the role must be provisioned there — O-2335),
// so finer roles reach enforcement.
const ROLE_BY_SLUG = Object.fromEntries(ROLE_CATALOG.map((role) => [role.slug, role]));
const ROLE_OPTIONS = ROLE_CATALOG.map((r) => ({ value: r.slug, label: r.label }));

// Fallback for a membership whose slug doesn't resolve to a built-in —
// mirrors the backend `canonical_role_slug` default (the enforcement
// resolver's default role). The backend already collapses to this, so it's
// belt-and-braces for an older backend that omits `role_slug`.
const DEFAULT_ROLE_SLUG = "viewer";


// A roster read is not an action-validation surface: raw WorkOS/gateway
// details are neither actionable nor safe UI copy. This helper also gives the
// component test a stable seam for the failure state rendered below.
export function userListFailureMessage(err, caps) {
    return denialCopy(err, caps).message;
}

// ── Row shape ────────────────────────────────────────────────────

export function flattenUser(u) {
    const first = u.firstName ?? u.first_name ?? "";
    const last  = u.lastName  ?? u.last_name  ?? "";
    return {
        userId:        u.userId       ?? u.user_id       ?? "",
        membershipId:  u.membershipId ?? u.membership_id ?? "",
        email:         u.email ?? "",
        firstName:     first,
        lastName:      last,
        name:          [first, last].filter(Boolean).join(" "),
        // Canonical kebab RBAC slug. grpc-gateway camelCases proto field
        // names; tolerate either. If a backend predates the `role_slug`
        // field, fall back to the deprecated two-value enum so admins don't
        // silently render as Viewer during a version-skew window — only an
        // absent role entirely defaults to viewer.
        roleSlug:      u.roleSlug ?? u.role_slug ?? legacyRoleToSlug(u.role),
    };
}

// Map the deprecated two-value ProtoTenantRole to a canonical slug, for the
// version-skew fallback only (a backend that predates `role_slug`). Admin →
// tenant-admin; everything else (Member / unspecified / absent) → viewer.
function legacyRoleToSlug(role) {
    return role === "TENANT_ROLE_ADMIN" ? "tenant-admin" : DEFAULT_ROLE_SLUG;
}

// ── Cells ────────────────────────────────────────────────────────

function RoleBadge({ roleSlug }) {
    const entry = ROLE_BY_SLUG[roleSlug];
    // Tenant admin is the only privileged-by-default role, so it reads in ink;
    // every finer role reads muted.
    const isAdmin = roleSlug === "tenant-admin";
    return (
        <span
            title={entry?.description ?? undefined}
            className={`inline-flex max-w-full items-center justify-self-start truncate rounded-[5px] border px-[7px] py-[2px] text-[12px] leading-[1.35] ${isAdmin
                ? "border-[color:var(--line-strong)] font-medium text-[color:var(--ink)]"
                : "border-[color:var(--line)] text-[color:var(--muted)]"}`}
        >
            {roleLabel(roleSlug)}
        </span>
    );
}

// ── Invite form ──────────────────────────────────────────────────

export const EMPTY_ROSTER_NOTE = "Use “Invite user” to bring teammates onto this tenant.";

export function InviteForm({ onSubmit, busy, error, localAuth = usesLocalAuth() }) {
    const [email, setEmail] = useState("");
    const [roleSlug, setRoleSlug] = useState(DEFAULT_ROLE_SLUG);

    function submit(e) {
        e.preventDefault();
        const trimmed = email.trim();
        if (!trimmed) return;
        onSubmit({ email: trimmed, roleSlug });
    }

    return (
        <form onSubmit={submit} className="grid gap-[14px]">
            <Field label="Email">
                <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="someone@example.com"
                    className={FIELD}
                />
            </Field>
            <Field as="div" label="Role" hint={ROLE_BY_SLUG[roleSlug]?.description}>
                <Select
                    value={roleSlug}
                    onChange={setRoleSlug}
                    options={ROLE_OPTIONS}
                    ariaLabel="Role"
                />
            </Field>
            <p className={NOTE}>{ROLE_ACTIVITY_ACCESS_NOTE}</p>
            <p className={NOTE}>
                {localAuth
                    ? "The email must belong to an existing account. Adding the user gives that account access to this tenant. No invitation email is sent."
                    : "The user receives an invitation email and joins this tenant after accepting it."}
            </p>
            <ErrorText>{error}</ErrorText>
            <div className="flex items-center justify-end">
                <Button type="submit" variant="primary" disabled={busy}>
                    {localAuth ? (busy ? "Adding…" : "Add user") : (busy ? "Inviting…" : "Send invite")}
                </Button>
            </div>
        </form>
    );
}

export function InvitationSuccessMessage({ resent, localAuth = usesLocalAuth() }) {
    return (
        <p className="m-0 text-[13px] leading-[1.5] text-[color:var(--muted)]">
            {localAuth
                ? "The existing account has been added to this tenant. No invitation email was sent."
                : resent
                  ? "A previous pending invitation was replaced. The invitee must use the newest email link."
                  : "The recipient can access this tenant after accepting the invitation."}
        </p>
    );
}

// ── Page ─────────────────────────────────────────────────────────

const probeLegacyAccessTransport = () => listGrantRoles({ pageSize: 1 });
const probeActivityAccessTransport = () => listActivityGrants();

function useAccessTransportAvailability(caps, probe) {
    const authIdentityEpoch = useSyncExternalStore(
        onAuthIdentityChange,
        getAuthIdentityEpoch,
        getAuthIdentityEpoch,
    );
    const eligible = hasPermission(caps, PERMISSIONS.GRANTS_READ);
    const [state, setState] = useState(() => ({ authIdentityEpoch, status: "pending" }));

    useEffect(() => {
        let cancelled = false;
        if (!eligible) {
            setState({ authIdentityEpoch, status: "unavailable" });
            return () => { cancelled = true; };
        }

        setState({ authIdentityEpoch, status: "pending" });
        probe()
            .then(() => {
                if (!cancelled) setState({ authIdentityEpoch, status: "ready" });
            })
            .catch(() => {
                if (!cancelled) setState({ authIdentityEpoch, status: "unavailable" });
            });
        return () => { cancelled = true; };
    }, [authIdentityEpoch, eligible, probe]);

    return state.authIdentityEpoch === authIdentityEpoch ? state.status : "pending";
}

function UsersTab({ onOpenGrant, accessAvailable }) {
    const localAuth = usesLocalAuth();
    const me = useCurrentUser();
    const currentUserResolved = Boolean(me);
    // TenantUser.user_id is the WorkOS user ID, while /v1/me.user_id is
    // Origin's internal UUID. Match against the identity-provider subject so
    // the caller's own row cannot expose actions the gateway will reject.
    const myIdentitySub = me?.identitySub ?? null;

    // Role-change / remove / reset-password are all management actions.
    // Access inspection has its own read permissions and does not authorize
    // any of these management actions.
    // (Full multi-binding role editing is a follow-up; today this gates
    // the existing single-role affordances.)
    const caps = useMyPermissions();
    const capsRef = useRef(caps);
    capsRef.current = caps;
    const canManageUsers = hasPermission(caps, PERMISSIONS.USERS_MANAGE);
    const canInspectAccess = accessAvailable
        && hasSensitivePermission(caps, PERMISSIONS.GRANTS_READ)
        && hasSensitivePermission(caps, PERMISSIONS.DIRECTORY_DATA_READ);
    const [accessUser, setAccessUser] = useState(null);
    useEffect(() => {
        if (!canInspectAccess) setAccessUser(null);
    }, [canInspectAccess]);

    const [rows, setRows] = useState([]);
    const [nextCursor, setNextCursor] = useState(null);
    // True from mount: the first read starts in the effect below, and until
    // it answers there is no count.
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState("");
    const deferredSearch = useDeferredValue(search);

    const [overlay, setOverlay] = useState(null); // null | "invite" | "success"
    const [inviteBusy, setInviteBusy] = useState(false);
    const [inviteError, setInviteError] = useState(null);
    // True when the backend revoked and replaced an existing pending invite,
    // so the success dialog can warn the invitee's earlier link is now dead.
    const [inviteResent, setInviteResent] = useState(false);

    // The row action being confirmed: { kind: "role" | "password" | "remove", row },
    // and the menu trigger that opened it, which gets focus back on close.
    const [action, setAction] = useState(null);
    const [actionBusy, setActionBusy] = useState(false);
    const [actionError, setActionError] = useState(null);
    const actionTrigger = useRef(null);

    const fetchPage = useCallback(async ({ replace = false, after = null } = {}) => {
        setLoading(true);
        setError(null);
        try {
            const params = { page_size: PAGE_SIZE };
            if (after) params.after = after;
            const resp = await restGet("/v1/users", params);
            const list = (resp?.users ?? []).map(flattenUser);
            setRows((prev) => (replace ? list : [...prev, ...list]));
            // grpc-gateway camelCases proto field names; tolerate either.
            setNextCursor(resp?.nextCursor ?? resp?.next_cursor ?? null);
        } catch (err) {
            setError(userListFailureMessage(err, capsRef.current));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            await fetchPage({ replace: true, after: null });
            if (cancelled) return;
        })();
        return () => { cancelled = true; };
    }, [fetchPage]);

    const handleInvite = useCallback(async ({ email, roleSlug }) => {
        setInviteBusy(true);
        setInviteError(null);
        try {
            // Send the canonical kebab slug; the backend writes it to WorkOS
            // (the legacy `role` enum is left unset — `role_slug` wins).
            const res = await restPost("/v1/users:invite", { email, roleSlug });
            setInviteResent(Boolean(res?.resent));
            setOverlay("success");
            // Refetch from the start so the new pending member shows
            // up if WorkOS surfaces them in the list immediately. Some
            // backends only show accepted users — refetching is still
            // the right thing because the gateway is the source of
            // truth.
            await fetchPage({ replace: true, after: null });
        } catch (err) {
            console.error("[user management] invite", err);
            setInviteError(denialOrRawMessage(err, caps));
        } finally {
            setInviteBusy(false);
        }
    }, [fetchPage, caps]);

    const openAction = useCallback((kind, row, trigger) => {
        actionTrigger.current = trigger;
        setActionError(null);
        setAction({ kind, row });
    }, []);

    const closeAction = useCallback(() => {
        if (actionBusy) return;
        setAction(null);
        setActionError(null);
        const trigger = actionTrigger.current;
        if (trigger?.isConnected) trigger.focus();
    }, [actionBusy]);

    const removeUser = useCallback(async (membershipId) => {
        await restDelete(`/v1/users/${encodeURIComponent(membershipId)}`);
        setRows((prev) => prev.filter((r) => r.membershipId !== membershipId));
    }, []);

    // Admin-initiated password reset for another user. The proto
    // ChangePasswordRequest accepts an optional target_membership_id —
    // the backend validates that the membership belongs to the caller's
    // org before acting. current_password must be OMITTED on this path.
    const resetPassword = useCallback(async (membershipId, newPassword) => {
        await restPost("/v1/user-management/change-password", {
            target_membership_id: membershipId,
            new_password: newPassword,
        });
    }, []);

    const changeRole = useCallback(async (membershipId, newRoleSlug) => {
        // grpc-gateway's PATCH route binds the JSON body to the proto
        // message; send the canonical kebab slug as `roleSlug` so the
        // backend assigns the real RBAC role. authedFetch handles the
        // bearer + 401 refresh; restClient doesn't expose a named PATCH
        // helper, so call authedFetch directly.
        const res = await authedFetch(`/v1/users/${encodeURIComponent(membershipId)}/role`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ roleSlug: newRoleSlug }),
        });
        if (!res.ok) {
            const text = await res.text().catch(() => "");
            throw new Error(`PATCH /v1/users/.../role ${res.status}: ${text || res.statusText}`);
        }
        setRows((prev) =>
            prev.map((r) =>
                r.membershipId === membershipId ? { ...r, roleSlug: newRoleSlug } : r,
            ),
        );
    }, []);

    // The dialog stays open while the call runs and shows a failure in place;
    // it closes once the change has landed.
    const confirmAction = useCallback(async (value) => {
        if (!action || actionBusy) return;
        const { kind, row } = action;
        setActionBusy(true);
        setActionError(null);
        try {
            if (kind === "remove") await removeUser(row.membershipId);
            else if (kind === "password") await resetPassword(row.membershipId, value);
            else await changeRole(row.membershipId, value);
            setAction(null);
            const trigger = actionTrigger.current;
            if (trigger?.isConnected) trigger.focus();
        } catch (err) {
            console.error(`[user management] ${kind}`, err);
            setActionError(denialOrRawMessage(err, capsRef.current));
        } finally {
            setActionBusy(false);
        }
    }, [action, actionBusy, removeUser, resetPassword, changeRole]);

    // Client-side filter over loaded rows. The /v1/users endpoint
    // doesn't accept a search filter (just cursor pagination), so —
    // same as the endpoint inventory — we filter what we have. Load
    // more pages to widen the candidate set.
    const filteredRows = useMemo(() => {
        const q = deferredSearch.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((r) => {
            if (r.email?.toLowerCase().includes(q)) return true;
            if (r.name?.toLowerCase().includes(q)) return true;
            if (r.firstName?.toLowerCase().includes(q)) return true;
            if (r.lastName?.toLowerCase().includes(q)) return true;
            // Match on both the human label ("SecOps") and the raw slug
            // ("sec-ops") so either spelling finds the row.
            if (roleLabel(r.roleSlug).toLowerCase().includes(q)) return true;
            if (r.roleSlug?.toLowerCase().includes(q)) return true;
            return false;
        });
    }, [rows, deferredSearch]);
    const isFiltering = deferredSearch.trim().length > 0;

    const tableColumns = useMemo(
        () => getUserManagementColumns(canManageUsers, canInspectAccess),
        [canManageUsers, canInspectAccess],
    );
    const cols = useDataTableColumns({
        storageKey: "userManagement",
        columns: tableColumns,
        defaultVisible: tableColumns.filter((column) => column.id !== "membership").map((column) => column.id),
    });

    const renderCell = useCallback(
        (r, col) => {
            switch (col.id) {
                case "email":
                    return (
                        <OverflowTooltip
                            as="strong"
                            text={r.email || "—"}
                            className="text-[12.5px] leading-[1.4] font-medium text-[color:var(--ink)]"
                        />
                    );
                case "name":
                    return (
                        <OverflowTooltip
                            as="span"
                            text={r.name || "—"}
                            className="text-[12.5px] leading-[1.4] text-[color:var(--muted)]"
                        />
                    );
                case "role":
                    return <RoleBadge roleSlug={r.roleSlug} />;
                case "membership":
                    return (
                        <OverflowTooltip
                            as="span"
                            text={r.membershipId}
                            className="text-[12px] leading-[1.4] font-mono text-[color:var(--muted)]"
                        />
                    );
                case "_actions": {
                    if (!canManageUsers && !canInspectAccess) return null;
                    const isSelf = Boolean(myIdentitySub && r.userId === myIdentitySub);
                    return (
                        <div className="flex flex-wrap items-center gap-x-[8px] gap-y-[6px]">
                            {canInspectAccess ? (
                                <Button size="sm" aria-haspopup="dialog" onClick={() => setAccessUser(r)}>View access</Button>
                            ) : null}
                            {canManageUsers && currentUserResolved ? (
                                <RowActions row={r} isSelf={isSelf} onAction={openAction} />
                            ) : null}
                        </div>
                    );
                }
                default:
                    return null;
            }
        },
        [myIdentitySub, openAction, canManageUsers, canInspectAccess, currentUserResolved],
    );

    const firstLoad = loading && rows.length === 0;
    // Null until the first read answers, so the bar doesn't claim "0 users".
    const count = firstLoad ? null : isFiltering ? filteredRows.length : rows.length;
    const loadMore = nextCursor ? (
        <Button size="sm" onClick={() => fetchPage({ after: nextCursor })} disabled={loading}>
            {loading ? "Loading…" : "Load more"}
        </Button>
    ) : null;

    return (
        <>
            {/* Inviting is a management action: hidden without manage. */}
            {canManageUsers ? (
                <SectionActions>
                    <button
                        type="button"
                        onClick={() => {
                            setInviteError(null);
                            setOverlay((cur) => (cur === "invite" ? null : "invite"));
                        }}
                        aria-pressed={overlay === "invite"}
                        className={SECTION_BUTTON}
                    >
                        {localAuth ? "Add user" : "Invite user"}
                    </button>
                </SectionActions>
            ) : null}
            <Listing
                count={count}
                error={error ? `User list unavailable. ${error}` : null}
                onRetry={() => fetchPage({ replace: true, after: null })}
                retrying={loading}
                filtered={isFiltering}
                bar={(
                    <>
                        <SearchInput
                            size="sm"
                            value={search}
                            onChange={setSearch}
                            placeholder="Search email, name or role"
                            label="Search users"
                            className="w-[260px] max-w-full"
                        />
                        {isFiltering ? (
                            <>
                                <Count n={count} one="match" many="matches" />
                                <span>of {rows.length.toLocaleString("en-US")} loaded</span>
                            </>
                        ) : (
                            <Count n={count} one="user" />
                        )}
                        {nextCursor ? <span>More to load</span> : null}
                    </>
                )}
                barEnd={<ColumnPicker {...cols.pickerProps} />}
                empty={{
                    title: "No users in this tenant",
                    note: localAuth ? 'Use "Add user" to add an existing account to this tenant.' : EMPTY_ROSTER_NOTE,
                }}
                noMatch={(
                    <>
                        No users match this search. Clear the search or load more pages to widen the result set.
                        {loadMore ? <> {loadMore}</> : null}
                    </>
                )}
                className="flex-1"
            >
                <DataTable
                    cols={cols}
                    rows={filteredRows}
                    getRowId={(r) => r.membershipId}
                    renderCell={renderCell}
                    loading={loading}
                    skeletonRows={4}
                    footer={loadMore ? <div className="grid place-items-center pt-[8px] pb-[10px]">{loadMore}</div> : null}
                />
            </Listing>

            <OverlayDialog
                open={overlay === "invite"}
                onClose={() => setOverlay(null)}
                analyticsId={DIALOG.USER_INVITE}
                eyebrow="Users"
                title={localAuth ? "Add user" : "Invite user"}
                width="560px"
            >
                <InviteForm
                    onSubmit={handleInvite}
                    busy={inviteBusy}
                    error={inviteError}
                    localAuth={localAuth}
                />
            </OverlayDialog>
            <OverlayDialog
                open={overlay === "success"}
                onClose={() => setOverlay(null)}
                analyticsId={DIALOG.USER_INVITED}
                eyebrow="Users"
                title={localAuth ? "User added" : "Invitation sent"}
                width="480px"
            >
                <InvitationSuccessMessage resent={inviteResent} localAuth={localAuth} />
            </OverlayDialog>
            {action && canManageUsers ? (
                <UserActionDialog
                    key={`${action.kind}:${action.row.membershipId}`}
                    action={action.kind}
                    row={action.row}
                    busy={actionBusy}
                    error={actionError}
                    onConfirm={confirmAction}
                    onCancel={closeAction}
                />
            ) : null}
            {accessUser && canInspectAccess ? (
                <UserEffectiveAccessDialog key={accessUser.userId} user={accessUser} onOpenGrant={onOpenGrant} onClose={() => setAccessUser(null)} />
            ) : null}
        </>
    );
}

// Access control: role grants and activity grants are two ways a tenant may
// be set up, and each shows when its transport answers. With both, they sit
// behind tabs, activity grants first: they are what "View grant" in a
// user's access opens onto.
const ACCESS_PARTS = [
    { id: "activity", label: "Activity grants" },
    { id: "roles", label: "Role grants" },
];

export default function UserManagementPage({ tab = "users", onOpenVisibility = null }) {
    const [selectedGrant, setSelectedGrant] = useState(null);
    const [part, setPart] = useState(ACCESS_PARTS[0].id);
    const authIdentityEpoch = useSyncExternalStore(onAuthIdentityChange, getAuthIdentityEpoch, getAuthIdentityEpoch);
    useEffect(() => setSelectedGrant(null), [authIdentityEpoch]);
    const caps = useMyPermissions();
    const legacyAccessTransport = useAccessTransportAvailability(caps, probeLegacyAccessTransport);
    const activityAccessTransport = useAccessTransportAvailability(caps, probeActivityAccessTransport);

    if (tab === "visibility") {
        if (legacyAccessTransport === "pending" || activityAccessTransport === "pending") {
            return (
                <div className="py-[8px]" aria-busy="true">
                    <SkeletonLines lines={4} label="Loading access rules…" />
                </div>
            );
        }
        const legacy = legacyAccessTransport === "ready";
        const activity = activityAccessTransport === "ready";
        if (!legacy && !activity) {
            return <p className="m-0 py-[8px] text-[13px] text-[color:var(--muted)]">This organization has no access rules to set.</p>;
        }
        const roles = <AccessTab key={`grants:${authIdentityEpoch}`} selectedGrant={selectedGrant} onClearSelectedGrant={() => setSelectedGrant(null)} />;
        const grants = <ActivityAccessTab key={`activity:${authIdentityEpoch}`} />;
        if (!legacy || !activity) return legacy ? roles : grants;
        return (
            <div className="flex min-w-0 flex-1 flex-col gap-[16px]">
                <Tabs items={ACCESS_PARTS} value={part} onChange={setPart} label="Access control" className="self-start" />
                {part === "roles" ? roles : grants}
            </div>
        );
    }
    return <UsersTab key={authIdentityEpoch} onOpenGrant={() => { setSelectedGrant(null); onOpenVisibility?.(); }} accessAvailable={legacyAccessTransport === "ready"} />;
}

// ── Row actions ──────────────────────────────────────────────────

/**
 * What Change role, Reset password and Remove ask before they act on a row.
 * `action` is "role" | "password" | "remove"; `onConfirm` receives the new
 * role slug or password (nothing for remove).
 */
export function UserActionDialog({ action, row, busy = false, error = null, onConfirm, onCancel }) {
    const [role, setRole] = useState(row.roleSlug);
    const [password, setPassword] = useState("");
    const passwordRef = useRef(null);
    // The confirm button takes focus as the dialog opens; the password
    // dialog's field is what the reader types into first.
    useEffect(() => {
        if (action === "password") passwordRef.current?.focus();
    }, [action]);

    if (action === "remove") {
        return (
            <ConfirmDialog
                open
                analyticsId={DIALOG.USER_REMOVE}
                eyebrow={row.email}
                title="Remove user?"
                body={(
                    <div className="grid gap-[8px]">
                        <p className={NOTE}>This removes their access to this tenant.</p>
                        <ErrorText>{error}</ErrorText>
                    </div>
                )}
                confirmLabel={busy ? "Removing…" : "Remove user"}
                confirmDisabled={busy}
                destructive
                onConfirm={() => onConfirm()}
                onCancel={onCancel}
            />
        );
    }
    if (action === "password") {
        return (
            <ConfirmDialog
                open
                analyticsId={DIALOG.USER_PASSWORD_RESET}
                eyebrow={row.email}
                title="Reset password"
                body={(
                    <div className="grid gap-[8px]">
                        <Field label="New password" error={error}>
                            <input
                                ref={passwordRef}
                                type="password"
                                value={password}
                                autoComplete="new-password"
                                onChange={(e) => setPassword(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && password && !busy) { e.preventDefault(); onConfirm(password); }
                                }}
                                className={FIELD}
                            />
                        </Field>
                    </div>
                )}
                confirmLabel={busy ? "Resetting…" : "Reset password"}
                confirmDisabled={busy || !password}
                onConfirm={() => onConfirm(password)}
                onCancel={onCancel}
            />
        );
    }
    const unchanged = role === row.roleSlug;
    return (
        <ConfirmDialog
            open
            analyticsId={DIALOG.USER_ROLE_CHANGE}
            eyebrow={row.email}
            title="Change role"
            body={(
                <div className="grid gap-[12px]">
                    <Field as="div" label="New role" hint={ROLE_BY_SLUG[role]?.description}>
                        <Select value={role} onChange={setRole} options={ROLE_OPTIONS} ariaLabel="New role" />
                    </Field>
                    <p className={NOTE}>{ROLE_ACTIVITY_ACCESS_NOTE} Changing this role does not change activity grants.</p>
                    <ErrorText>{error}</ErrorText>
                </div>
            )}
            confirmLabel={busy ? "Changing…" : "Change role"}
            confirmDisabled={busy || unchanged}
            onConfirm={() => onConfirm(role)}
            onCancel={onCancel}
        />
    );
}

const ROW_ACTIONS = [
    { kind: "role", label: "Change role" },
    { kind: "password", label: "Reset password" },
    { kind: "remove", label: "Remove" },
];

// A row's "…" menu. It portals to the body so the table's clipped cells
// cannot cut it off. The caller's own row says "You" instead.
function RowActions({ row, isSelf, onAction }) {
    const [open, setOpen] = useState(false);
    const trigger = useRef(null);
    const menu = useRef(null);
    const [position, setPosition] = useState({ top: 0, left: 0 });
    useLayoutEffect(() => {
        if (!open) return;
        const place = () => {
            const box = trigger.current?.getBoundingClientRect();
            if (box) setPosition({ top: Math.max(8, Math.min(box.bottom + 4, window.innerHeight - 150)), left: Math.max(8, Math.min(box.right - 190, window.innerWidth - 198)) });
        };
        place();
        window.addEventListener("resize", place);
        window.addEventListener("scroll", place, true);
        return () => { window.removeEventListener("resize", place); window.removeEventListener("scroll", place, true); };
    }, [open]);
    useEffect(() => {
        if (!open) return;
        menu.current?.querySelector('[role="menuitem"]')?.focus();
        const outside = (event) => {
            if (!menu.current?.contains(event.target) && !trigger.current?.contains(event.target)) setOpen(false);
        };
        document.addEventListener("pointerdown", outside);
        return () => document.removeEventListener("pointerdown", outside);
    }, [open]);
    if (isSelf) return <span className="text-[12px] text-[color:var(--muted)]">You</span>;
    const choose = (kind) => { setOpen(false); onAction(kind, row, trigger.current); };
    return <>
        <button ref={trigger} type="button" aria-label={`More actions for ${row.email}`} aria-haspopup="menu" aria-expanded={open}
            className={ICON_BUTTON}
            onClick={() => setOpen((value) => !value)} onKeyDown={(event) => {
                if (event.key === "ArrowDown") { event.preventDefault(); setOpen(true); }
            }}><EllipsisHorizontalIcon aria-hidden="true" className="h-[18px] w-[18px]" /></button>
        {open ? createPortal(<div ref={menu} role="menu" aria-label={`Actions for ${row.email}`} style={position}
            className={`${MENU} fixed z-[10001] grid w-[190px] gap-[2px] p-[5px]`}
            onKeyDown={(event) => {
                const items = [...menu.current.querySelectorAll('[role="menuitem"]')];
                const index = items.indexOf(document.activeElement);
                if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
                    event.preventDefault();
                    const next = event.key === "Home" ? 0 : event.key === "End" ? items.length - 1 : (index + (event.key === "ArrowDown" ? 1 : -1) + items.length) % items.length;
                    items[next]?.focus();
                } else if (event.key === "Escape" || event.key === "Tab") {
                    event.preventDefault(); setOpen(false); trigger.current?.focus();
                }
            }}>
            {ROW_ACTIONS.map(({ kind, label }) =>
                <button key={kind} role="menuitem" type="button"
                    className={`${MENU_ITEM} outline-none focus:bg-[color-mix(in_srgb,var(--ink)_8%,transparent)] focus:text-[color:var(--ink)] ${kind === "remove" ? "!text-[color:var(--danger)]" : ""}`}
                    onClick={() => choose(kind)}>{label}</button>)}
        </div>, document.body) : null}
    </>;
}
